import type {
  GVIDefect,
  GVIDefectGroup,
  GVIDefectGroupingMeta,
} from 'types/fde/aerospace/aerospaceGVI'
import type { CadModelAlignment } from 'features/aerospaceGVI/types/cadModelAlignment'
import { RolodexEntryJson } from 'types/rolodex/rolodexEntries'
import { parseBoolean, parseNumber, parseString } from 'utils/parsingHelpers'
import { compareAlphanumeric } from 'utils/stringHelpers'

import { GeckoEntryBase } from './geckoEntry'

/**
 * The inspection type for Aerospace GVI inspections
 */
export enum AerospaceGVIInspectionType {
  DRONE = 'Drone',
  MANUAL = 'Manual',
  BOTH = 'Both',
}

/**
 * The review status for Aerospace GVI inspections
 */
export enum AerospaceGVIStatus {
  PENDING = 'Pending',
  REVIEWED = 'Reviewed',
}

function parseInspectionType(
  value: unknown
): AerospaceGVIInspectionType | undefined {
  if (typeof value !== 'string') return undefined
  const normalized = value.toLowerCase()
  if (normalized === 'drone') return AerospaceGVIInspectionType.DRONE
  if (normalized === 'manual') return AerospaceGVIInspectionType.MANUAL
  if (normalized === 'both') return AerospaceGVIInspectionType.BOTH
  return undefined
}

function parseStatus(value: unknown): AerospaceGVIStatus | undefined {
  if (typeof value !== 'string') return undefined
  const normalized = value.toLowerCase()
  if (normalized === 'pending') return AerospaceGVIStatus.PENDING
  if (normalized === 'reviewed') return AerospaceGVIStatus.REVIEWED
  return undefined
}

/**
 * An Aerospace General Visual Inspection entry.
 *
 * Stores inspection metadata, defect analysis results, and references to
 * associated 3D model and camera pose entries.
 */
export class GeckoAerospaceGVI extends GeckoEntryBase<
  {
    tailId: string
    date: number
    inspectionType: AerospaceGVIInspectionType
    status: AerospaceGVIStatus
    facility?: string
    imageCount?: number
    defects?: GVIDefect[]
    defectGroups?: GVIDefectGroup[]
    defectGroupingMeta?: GVIDefectGroupingMeta
    /** Rolodex entry ID of the aerospace-gvi-model providing the 3D OBJ. */
    cadModelEntryId?: string
    /** Computed CAD model alignment (from the alignment pipeline). */
    cadModelAlignment?: CadModelAlignment
    photosEnabled: boolean
  },
  {
    sandbox?: string
    camerasEntry?: string
  }
> {
  private constructor(json: RolodexEntryJson) {
    const recordData = json.data as Record<string, unknown>

    const defects = Array.isArray(recordData.defects)
      ? (recordData.defects as GVIDefect[])
      : undefined
    const defectGroups = Array.isArray(recordData.defectGroups)
      ? (recordData.defectGroups as GVIDefectGroup[])
      : undefined
    const defectGroupingMeta =
      recordData.defectGroupingMeta as GVIDefectGroupingMeta | undefined

    super(
      json,
      {
        tailId: parseString(recordData.tailId) ?? '',
        date: parseNumber(recordData.date) ?? Date.now(),
        inspectionType:
          parseInspectionType(recordData.inspectionType) ??
          AerospaceGVIInspectionType.DRONE,
        status: parseStatus(recordData.status) ?? AerospaceGVIStatus.PENDING,
        facility: parseString(recordData.facility),
        imageCount: parseNumber(recordData.imageCount),
        defects,
        defectGroups,
        defectGroupingMeta,
        cadModelEntryId:
          parseString(recordData.cadModelEntryId) ??
          parseString(recordData.modelEntryId),
        cadModelAlignment: recordData.cadModelAlignment as
          | CadModelAlignment
          | undefined,
        photosEnabled: parseBoolean(recordData.photosEnabled) ?? false,
      },
      {
        sandbox: parseString(json.tags.sandbox),
        camerasEntry: parseString(json.tags['aerospace-gvi-cameras']),
      },
      {}
    )
  }

  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoAerospaceGVI(json)
  }

  public static DefaultSort(a: GeckoAerospaceGVI, b: GeckoAerospaceGVI) {
    return compareAlphanumeric(a.data.tailId, b.data.tailId)
  }
}
